import React, { useEffect, useState } from "react"
import { Typography, TextField, Button, Snackbar, Alert } from "@mui/material"
import { useNavigate } from "react-router-dom"
import PasswordField from "./PasswordField"
import api from "../utils/api"

function generatePassword() {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz"
  const numbers = "0123456789"
  const specialChars = "!@#$%^&*"

  const pickRandom = (set: string) =>
    set.charAt(Math.floor(Math.random() * set.length))

  return (
    pickRandom(chars) +
    pickRandom(numbers) +
    pickRandom(specialChars) +
    Array.from({ length: Math.floor(Math.random() * 8) + 3 }, () =>
      pickRandom(chars + numbers + specialChars)
    ).join("")
  )
}

export function CreateAppAdminForm() {
  // States
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [name, setName] = useState("")
  const [phone, setPhone] = useState("")
  const [isUsernameAvailable, setIsUsernameAvailable] = useState(true)
  const [currentAdmins, setCurrentAdmins] = useState<string[]>([])
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")

  const navigate = useNavigate()

  useEffect(() => {
    async function fetchAdmins() {
      try {
        const response = await api.get("/admin?type=appAdmin")
        setCurrentAdmins(response.data)
      } catch (error) {
        console.error("Error fetching admins:", error)
      }
    }

    fetchAdmins()
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setUsername(value.replace(/\s/g, ""))
    const adminsArray = Array.isArray(currentAdmins) ? currentAdmins : []
    setIsUsernameAvailable(!adminsArray.includes(value.trim()))
  }

  const handleSubmit = async () => {
    if (phone && !isValidPhone(phone)) return
    if (!username || !password || !name || !phone) {
      setErrorMessage("All fields are required")
      setOpenSnackbar(true)
      return
    }

    try {
      await api.post("/admin/app-admin", {
        username,
        password,
        name,
        phone,
        type: "appAdmin",
      })

      navigate("/admin/dashboard", {
        state: { notification: "App admin successfully created!" },
      })
    } catch (error) {
      // Extracting error message from response, if available
      const message =
        error.response?.data?.message || "An unexpected error occurred"

      setErrorMessage(
        error.response?.status === 400 && message === "Username already exists"
          ? "Username already exists"
          : message
      )
      setOpenSnackbar(true)
    }
  }

  const handleSnackbarClose = () => {
    setOpenSnackbar(false)
    setErrorMessage("")
  }
  const isValidPhone = (phone: string) => /^(\d{10})$/.test(phone)
  return (
    <>
      <Typography variant="h6" m={4}>
        Create An App Admin
      </Typography>
      {/* Username Field */}
      <TextField
        label="Username"
        variant="outlined"
        fullWidth
        margin="normal"
        value={username}
        required
        error={!isUsernameAvailable}
        helperText={!isUsernameAvailable ? "Username already taken." : ""}
        onChange={handleInputChange}
      />

      {/* Name Field */}
      <TextField
        label="Name"
        variant="outlined"
        fullWidth
        margin="normal"
        value={name}
        required
        onChange={(e) => setName(e.target.value)}
      />

      {/* Phone Field */}
      <TextField
        label="Phone"
        variant="outlined"
        fullWidth
        margin="normal"
        value={phone}
        required
        onChange={(e) => setPhone(e.target.value)}
        error={!!(phone && !/^(\d{10})$/.test(phone))} // Using double bang to ensure a boolean value
        helperText={
          phone && !/^(\d{10})$/.test(phone)
            ? "Please enter a 10-digit number."
            : ""
        }
        inputProps={{
          pattern: "\\d{10}", // Allow only 10-digit numbers
        }}
      />

      {/* Password Field */}
      <PasswordField
        password={password}
        setPassword={setPassword}
        generatePassword={generatePassword}
      />

      {/* Submit Button */}
      <Button
        variant="contained"
        color="primary"
        fullWidth
        onClick={handleSubmit}
        style={{ marginTop: "1em" }}
      >
        Create App Admin
      </Button>

      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  )
}
